import { useCallback, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { isBadRequestError } from '@/core/api/badRequestError';
import { paymentCreationService } from '@/entities/Payment/api/paymentCreationService';
import { createPaymentFormToRequest } from '@/entities/Payment/createPaymentMappers';
import type { CreatePaymentFormValues } from '@/entities/Payment/model/createPaymentSchema';
import { getErrorMessage } from '@/shared/lib/errors';

type PaymentResult = { kind: 'link'; url: string } | { kind: 'subscription' } | null;

/** Формирует платеж и завершает сценарий оплатой абонементом или ссылкой. */
export const useCreatePaymentSubmit = (
    orderId: string,
    form: UseFormReturn<CreatePaymentFormValues>,
) => {
    const [paymentId, setPaymentId] = useState<string | null>(null);
    const [result, setResult] = useState<PaymentResult>(null);

    /** Отправляет платеж и продолжает его оформление после частичного успеха. */
    const onSubmit = useCallback(async (values: CreatePaymentFormValues) => {
        form.clearErrors('root.server');

        try {
            let currentPaymentId = paymentId;

            if (!currentPaymentId) {
                const payment = await paymentCreationService.create(createPaymentFormToRequest(values, orderId));

                if (!payment.id) throw new Error('Не удалось получить идентификатор платежа');

                currentPaymentId = payment.id;
                setPaymentId(currentPaymentId);
                form.reset(values);
            }

            const shouldGenerateUrl = await paymentCreationService.shouldGenerateUrl(currentPaymentId);

            if (!shouldGenerateUrl) {
                setResult({ kind: 'subscription' });

                return;
            }

            const url = await paymentCreationService.generateUrl(currentPaymentId);

            if (!url?.trim()) throw new Error('Не удалось получить ссылку на оплату');

            setResult({
                kind: 'link',
                url
            });
        } catch (error: unknown) {
            if (isBadRequestError(error)) return;

            form.setError('root.server', {
                type: 'server',
                message: getErrorMessage(error, 'Не удалось сформировать платеж'),
            });
        }
    }, [form, orderId, paymentId]);

    /** Очищает результат предыдущего формирования платежа. */
    const resetResult = useCallback(() => {
        setPaymentId(null);
        setResult(null);
    }, []);

    return {
        onSubmit,
        paymentId,
        result,
        resetResult
    };
};
